import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  UserIcon,
  CpuChipIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { doc, setDoc, onSnapshot, getDoc } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { useAuth } from '../../context/AuthContext';

import PersonalityAssessmentForm from './PersonalityAssessmentForm';
import MindsetAssessmentForm from './MindsetAssessmentForm';
import CapabilitiesAssessmentForm from './CapabilitiesAssessmentForm';
import BusinessStageAssessmentForm from './BusinessStageAssessmentForm';

const PROFILING_COLLECTION = 'entrepreneurProfiles';

const EntrepreneurProfilingDashboard = ({ userRole = 'entrepreneur', userId = null }) => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState({
    email: '',
    firstname: '',
    lastname: '',
    organization: '',
  });
  const [profilingData, setProfilingData] = useState({
    personality: {},
    mindset: {},
    capabilities: {},
    businessStage: {},
    overallScore: 0,
    lastUpdated: null,
    userInfo: {},
  });

  const targetUserId = userId || (userRole === 'client' ? 'current' : 'current');

  const fetchUserInfo = useCallback(async () => {
    if (!currentUser?.uid) return;

    try {
      const clientRef = doc(firestore, 'clients', currentUser.uid);
      const clientSnap = await getDoc(clientRef);

      if (clientSnap.exists()) {
        const clientData = clientSnap.data();
        const userData = {
          email: clientData.email || currentUser.email || '',
          firstname: clientData.firstname || '',
          lastname: clientData.lastname || '',
          organization: clientData.organization || '',
        };
        setUserInfo(userData);
      } else {
        setUserInfo({
          email: currentUser.email || '',
          firstname: '',
          lastname: '',
          organization: '',
        });
      }
    } catch (error) {
      toast.error('Error fetching user info');
      setUserInfo({
        email: currentUser.email || '',
        firstname: '',
        lastname: '',
        organization: '',
      });
    }
  }, [currentUser]);

  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  useEffect(() => {
    setLoading(true);

    const profileRef = doc(firestore, PROFILING_COLLECTION, targetUserId);

    const unsubscribe = onSnapshot(
      profileRef,
      docSnap => {
        if (docSnap.exists()) {
          setProfilingData(docSnap.data());
        } else {
          const emptyData = {
            personality: {},
            mindset: {},
            capabilities: {},
            businessStage: {},
            overallScore: 0,
            lastUpdated: null,
          };
          setProfilingData(emptyData);
        }
        setLoading(false);
      },
      err => {
        toast.error('Error loading profiling data');
        const emptyData = {
          personality: {},
          mindset: {},
          capabilities: {},
          businessStage: {},
          overallScore: 0,
          lastUpdated: null,
        };
        setProfilingData(emptyData);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [targetUserId]);

  const handleSaveAssessment = async (category, data) => {
    try {
      const profileRef = doc(firestore, PROFILING_COLLECTION, targetUserId);

      const updatedData = {
        ...profilingData,
        [category]: data,
        lastUpdated: new Date().toISOString(),
        userId: targetUserId,
        userInfo: {
          email: userInfo.email,
          firstname: userInfo.firstname,
          lastname: userInfo.lastname,
          organization: userInfo.organization,
        },
      };

      updatedData.overallScore = calculateOverallScore(updatedData);

      await setDoc(profileRef, updatedData, { merge: true });
      setProfilingData(updatedData);
      toast.success(`${category} assessment saved successfully!`);
    } catch (error) {
      toast.error('Save assessment error');
    }
  };

  const calculateOverallScore = data => {
    const categories = ['personality', 'mindset', 'capabilities', 'businessStage'];

    let totalScore = 0;
    let categoryCount = 0;

    categories.forEach(category => {
      if (data[category] && data[category].score !== undefined) {
        totalScore += data[category].score;
        categoryCount++;
      }
    });

    return categoryCount > 0 ? Math.round(totalScore / categoryCount) : 0;
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'personality', name: 'Personality', icon: UserIcon },
    { id: 'mindset', name: 'Mindset', icon: CpuChipIcon },
    { id: 'capabilities', name: 'Capabilities', icon: AcademicCapIcon },
    { id: 'businessStage', name: 'Business Profiling', icon: BuildingOfficeIcon },
  ];

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading entrepreneur profiling...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <div className="mb-2 flex items-center gap-2 md:gap-3">
            <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-black md:text-3xl">
              Entrepreneurship Program Profiling Framework
            </h1>
          </div>
          <p className="text-sm text-black md:text-base">
            Comprehensive assessment of your entrepreneurial profile, mindset, capabilities, and
            business Profile
          </p>
          {profilingData.overallScore === 0 && (
            <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm text-blue-800">
                Welcome to the Entrepreneur Profiling Framework! Complete the assessments below to
                understand your entrepreneurial strengths and development areas.
              </p>
            </div>
          )}
        </div>

        <div className="mb-8 rounded-lg border border-blue-200 bg-white shadow-sm">
          <div className="border-b border-blue-200">
            <nav className="flex flex-wrap gap-2 px-4 md:space-x-8 md:px-6">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-black'
                      : 'border-transparent text-black hover:border-blue-300 hover:text-black'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-4 md:p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {activeTab === 'overview' && (
                  <ProfileOverview
                    profilingData={profilingData}
                    onCategoryClick={category => setActiveTab(category)}
                  />
                )}

                {activeTab === 'personality' && (
                  <PersonalityAssessmentForm
                    data={profilingData.personality}
                    onSave={data => handleSaveAssessment('personality', data)}
                    userRole={userRole}
                  />
                )}

                {activeTab === 'mindset' && (
                  <MindsetAssessmentForm
                    data={profilingData.mindset}
                    onSave={data => handleSaveAssessment('mindset', data)}
                    userRole={userRole}
                  />
                )}

                {activeTab === 'capabilities' && (
                  <CapabilitiesAssessmentForm
                    data={profilingData.capabilities}
                    onSave={data => handleSaveAssessment('capabilities', data)}
                    userRole={userRole}
                  />
                )}

                {activeTab === 'businessStage' && (
                  <BusinessStageAssessmentForm
                    data={profilingData.businessStage}
                    onSave={data => handleSaveAssessment('businessStage', data)}
                    userRole={userRole}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProfileOverview = ({ profilingData, onCategoryClick }) => {
  const { overallScore = 0 } = profilingData;
  const profileStatus = getProfileStatus(overallScore);

  const categoryScores = [
    {
      name: 'Personality Assessment',
      key: 'personality',
      score: profilingData.personality?.score || 0,
      color: 'blue',
      icon: UserIcon,
    },
    {
      name: 'Mindset Assessment',
      key: 'mindset',
      score: profilingData.mindset?.score || 0,
      color: 'green',
      icon: CpuChipIcon,
    },
    {
      name: 'Capabilities Assessment',
      key: 'capabilities',
      score: profilingData.capabilities?.score || 0,
      color: 'purple',
      icon: AcademicCapIcon,
    },
    {
      name: 'Business Profiling Assessment',
      key: 'businessStage',
      score: profilingData.businessStage?.score || 0,
      color: 'orange',
      icon: BuildingOfficeIcon,
    },
  ];

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-6 shadow-sm"
      >
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Entrepreneurial Profile Overview</h2>
            <p className="text-gray-600">
              Comprehensive assessment of your entrepreneurial profile
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">Last Updated</div>
            <div className="text-base font-semibold text-gray-900">
              {profilingData.lastUpdated
                ? new Date(profilingData.lastUpdated).toLocaleDateString()
                : 'Not assessed'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg bg-white p-6 shadow-sm">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Overall Profile Score</h3>
            </div>

            <div className="mb-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-3xl font-bold text-gray-900">{overallScore}%</span>
                <span
                  className={`rounded-full px-3 py-1 text-sm font-medium bg-${profileStatus.color}-100 text-${profileStatus.color}-800`}
                >
                  {profileStatus.status}
                </span>
              </div>
              <div className="h-3 w-full rounded-full bg-gray-200">
                <div
                  className={`h-3 rounded-full bg-${profileStatus.color}-500 transition-all duration-500`}
                  style={{ width: `${overallScore}%` }}
                />
              </div>
            </div>

            <div className="text-sm text-gray-600">{getProfileDescription(overallScore)}</div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow-sm">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Assessment Completion</h3>

            <div className="space-y-3">
              {categoryScores.map((category, index) => (
                <div key={category.key} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <category.icon className={`h-5 w-5 text-${category.color}-600`} />
                    <span className="text-sm text-gray-700">{category.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      {category.score > 0 ? `${category.score}%` : 'Not Started'}
                    </span>
                    {category.score > 0 && (
                      <div className="h-2 w-16 rounded-full bg-gray-200">
                        <div
                          className={`h-2 rounded-full bg-${category.color}-500`}
                          style={{ width: `${category.score}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => onCategoryClick('personality')}
              className="mt-4 w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              Start Assessment
            </button>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-6 text-xl font-semibold text-gray-900">Assessment Categories</h3>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {categoryScores.map((category, index) => (
            <motion.div
              key={category.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-4 transition-shadow hover:shadow-md"
              onClick={() => onCategoryClick(category.key)}
            >
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <category.icon className={`h-6 w-6 text-${category.color}-600`} />
                  <span className="text-sm font-medium text-gray-900">{category.name}</span>
                </div>
              </div>

              <div className="mb-2">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-lg font-bold text-gray-900">
                    {category.score > 0 ? `${category.score}%` : 'Not Started'}
                  </span>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-medium bg-${category.color}-100 text-${category.color}-800`}
                  >
                    {getCategoryStatus(category.score)}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-200">
                  <div
                    className={`h-2 rounded-full bg-${category.color}-500 transition-all duration-500`}
                    style={{ width: `${category.score}%` }}
                  />
                </div>
              </div>

              <div className="text-xs text-gray-600">Click to view detailed assessment</div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

const getProfileStatus = score => {
  if (score >= 75) return { status: 'Strong Entrepreneurial Profile', color: 'green' };
  if (score >= 60) return { status: 'Good Entrepreneurial Potential', color: 'blue' };
  if (score >= 45) return { status: 'Developing Entrepreneurial Profile', color: 'yellow' };
  return { status: 'Needs Development', color: 'red' };
};

const getCategoryStatus = score => {
  if (score >= 75) return 'Strong';
  if (score >= 60) return 'Good';
  if (score >= 45) return 'Developing';
  return score > 0 ? 'Basic' : 'Not Started';
};

const getProfileDescription = score => {
  if (score >= 75) {
    return 'You demonstrate strong entrepreneurial characteristics across all key areas. Focus on scaling and growth opportunities.';
  }
  if (score >= 60) {
    return 'You have good entrepreneurial potential with solid foundations. Continue developing key skills and mindset.';
  }
  if (score >= 45) {
    return 'You are developing entrepreneurial traits. Focus on strengthening core capabilities and mindset.';
  }
  return 'Complete the assessments to identify your entrepreneurial strengths and development areas.';
};

export default EntrepreneurProfilingDashboard;
